// R75 — THE TWO DAILY THINGS IN MY ROOM, AND ONE OF THEM SPENDS ON EVERY TAP.
//
// **THE COUNTER-EXAMPLE IS FOUR HUNDRED LINES AWAY IN THE SAME FILE, WHICH IS THE WHOLE ARGUMENT**
// (W12/R26/R35/R38: the capability lives only on the surface that needed it first). The daily CARD's
// draw reads its answer and says *"The card could not be drawn right now. Please try again."* The
// daily GUIDANCE's — the panel directly above it in the same room — was:
//
//     const data = await response.json().catch(() => ({}));
//     if (data.daily) setDaily(data.daily);
//     } catch { /* ignore */ }
//     setDailyStatus("idle");
//
// **SO SHE TAPS, THE BUTTON SAYS "READING…", THE BUTTON STOPS SAYING IT, AND NOTHING HAPPENS.**
// `handleDaily` refuses FIVE ways and every one was swallowed: 401, an empty `ensureUser`, *"Create
// your room first."*, *"Daily guidance is unavailable right now."* and a 500. **AND THIS IS THE ONE
// SWALLOWED REFUSAL ON THIS SITE THAT COSTS MONEY PER TAP:** `buildDailyGuidance` makes TWO
// AstrologyAPI calls and an Anthropic call, so a member with no feedback taps again, and again, and
// the owner pays for every one of them. R66's ruling, inverted — an act that did NOT land must not
// be silent either.
//
// **AND THE CARD IS ONLY HALF HONEST: ITS *READ* IS THE FORGERY.** `.then((r) => r.json())` with no
// status check at all, then `if (d.card) ... else setStatus("empty")` — so a 401, a 500 and an empty
// `ensureUser` all land on **"you have not drawn today"**, which is an invitation to tap the button
// that spends. A wrong sentence rather than a missing one (R60/R64's sharpest shape).
//
// **THE FOUR-WAY ANSWER IS RIGHT HERE AND THAT IS ARGUED THE OPPOSITE WAY FROM R73/R74** (R58's
// ruling is that the shape follows the route): both handlers read a signed session and both touch
// the database, so 401 and a genuine refusal are answers they really can give, and `sign-in` is the
// one a member fixes in a single tap (R61).
//
// **`none` IS A FIFTH VALUE AND IT IS NOT A REFUSAL.** A GET that lands with nothing cached is the
// normal state of this panel every morning; collapsing it into "refused" would put a failure
// sentence on her screen every day before she taps (R18, and W5-2/R39's third state).
//
// `doFetch` is a PARAMETER (R46), so every answer is reachable with no browser and no vendor.

export type DailyAnswer<T> =
  | { status: "ok"; value: T }
  | { status: "none" }
  | { status: "sign-in" }
  | { status: "refused" }
  | { status: "offline" };

// DL-3.2: `id` is the ROW this guidance is, and it rides on every answer the route gives. It is
// optional because a guidance stored before the morning job existed has no id in flight, and R18's
// distinction is the reason it is not defaulted to "" — absent and empty would then be one fact.
export type DailyGuidance = { id?: string; date: string; text: string };
export type DailyCard = { slug: string; name: string; orientation: string; interpretation?: string; overview?: string };

export const dailyGuidancePath = "/api/room/daily";
export const dailyCardPath = "/api/tarot/daily";

// ONE reader for BOTH routes, because they answer in one shape (R31: the crossing stops existing
// rather than being watched). The field name is the only thing that differs between them.
async function readDaily<T>(
  doFetch: typeof fetch,
  path: string,
  field: "daily" | "card",
  method: "GET" | "POST",
): Promise<DailyAnswer<T>> {
  let response: Response;
  try {
    // `no-store`: both of these are once-per-day facts, and a cached answer to "have I drawn today"
    // is how a member is shown yesterday's.
    response = await doFetch(path, method === "POST" ? { method: "POST" } : { cache: "no-store" });
  } catch {
    return { status: "offline" };
  }
  if (response.status === 401) { await response.body?.cancel?.().catch(() => {}); return { status: "sign-in" }; }
  const data = await response.json().catch(() => null) as (Record<string, unknown> & { ok?: boolean }) | null;
  if (!response.ok || !data) return { status: "refused" };
  // **R72's LINE, AND IT IS WHAT CLOSED THE EIGHTH AND NINTH ROUTES:** a body is the server's own
  // answer only when it says so. `ok: false` is a refusal even when the field rides with it (R70/R28).
  if (data.ok === false) return { status: "refused" };
  const value = data[field];
  // PRESENCE, never truthiness (R53). `null` here is the route's own "nothing today yet", which is a
  // real answer and the normal one — it is deliberately NOT folded into the refusal.
  if (value === null || value === undefined) return { status: "none" };
  return { status: "ok", value: value as T };
}

// ---------------------------------------------------------------------------
// DL-3.2 — **THE MORNING'S TAP IS A REQUEST FOR ONE PARTICULAR ROW.**
//
// The push payload's url is `/my-room?daily=<id>`, and until this the client read that as nothing at
// all: My Room opened Daily Guidance, which is the same row by the day's stability law — correct
// today, and correct by luck rather than by the id. A regeneration, a timezone seam or a member
// opening yesterday's notification are all cases where "today's" and "the one her line was cut from"
// come apart, and none of them has a symptom she could report.
//
// **THE READER OF `?daily=` IS WRITTEN TWICE ON PURPOSE AND HELD ACROSS THE BOUNDARY** (PN1's
// pattern): this file may import nothing, so it cannot share `server/daily-promise.js`'s
// `dailyIdInUrl`, and the two are driven over ONE table of urls by the fixture. A divergence between
// the side that WRITES the url and the side that READS it is the whole defect.
export function dailyIdFromSearch(search: string): string | null {
  const m = /[?&]daily=([^&#]*)/.exec(String(search || ""));
  if (!m) return null;
  const raw = decodeURIComponent(m[1]).trim();
  // An empty `?daily=` is not a row. Answering "" would send the room asking the server for one.
  return raw ? raw : null;
}

export function dailyGuidanceUrl(id: string | null = null): string {
  const clean = String(id || "").trim();
  return clean ? `${dailyGuidancePath}?id=${encodeURIComponent(clean)}` : dailyGuidancePath;
}

export const readDailyGuidance = (doFetch: typeof fetch = fetch, id: string | null = null) =>
  readDaily<DailyGuidance>(doFetch, dailyGuidanceUrl(id), "daily", "GET");
export const runDailyGuidance = (doFetch: typeof fetch = fetch) =>
  readDaily<DailyGuidance>(doFetch, dailyGuidancePath, "daily", "POST");
export const readDailyCard = (doFetch: typeof fetch = fetch) =>
  readDaily<DailyCard>(doFetch, dailyCardPath, "card", "GET");
export const drawDailyCard = (doFetch: typeof fetch = fetch) =>
  readDaily<DailyCard>(doFetch, dailyCardPath, "card", "POST");

// **WHICH SENTENCE THE PANEL SAYS, AS A LAW RATHER THAN AS A TERNARY AT THE CALL SITE.**
//
// `sign-in` is kept apart from the rest because it is the ONE she fixes in a single tap (R61) — the
// others all end in "try again". `none` and `ok` say NOTHING: a panel that apologises before she has
// asked for anything is a panel that apologises every morning.
//
// **THE SERVER'S OWN `message` IS DELIBERATELY NOT PRINTED HERE**, and there are two reasons rather
// than one. R72: a message is the server's own word only when it arrives beside `ok: false`, and
// these bodies carry no `ok` at all. 14-B4: this room turns into Spanish and those sentences are
// English server copy, so re-rendering them on the client is a second answer key (14-B3's seam).
// The one distinction they carried — *"Create your room first."* — is already drawn by this panel's
// OWN `daily.noProfile` arm, which is the only state in which that button does not render.
export function dailyNote<T>(answer: DailyAnswer<T> | null | undefined, failed: string, signIn: string) {
  if (!answer) return "";
  if (answer.status === "sign-in") return signIn;
  if (answer.status === "refused" || answer.status === "offline") return failed;
  return "";
}
